import { Client, Movement } from "types";

import { Grid } from "@mui/material";
import OperationsLogsCard from "./OperationsLogsCard";
import { useSession } from "providers/SessionProvider";
import { useEffect, useState } from "react";
import useCrud from "hooks/useCrud";
import { MOVEMENTS_URL } from "constants/urls";
import { useToast } from "hooks/useToast";
import { STATUS_CODES } from "constants/index";

type Props = {
  movements: Movement[];
  client: Client | null;
};

type OrderRestoreStatus = "available" | "not_available";

export default function OperationsLogs({ movements, client }: Props) {
  const { stores, users, associatedClients  } = useSession();
  const [movementList, setMovementList] = useState(movements);

  const { showSnackBar } = useToast();

  const {
    deleteData: deleteDraft,
    updateData: updateMovement,
    showToast,
    toastMessage,
    toastSeverity,
    setShowToast,
  } = useCrud<Movement>(MOVEMENTS_URL);

  const handleMovementUpdate = (updatedMovement: Movement) => {
    setMovementList((prev: any) =>
      prev.map((m: any) => (m.id === updatedMovement.id ? updatedMovement : m))
    );
  };

  const handleMovementDelete = (id: number) => {
    deleteDraft(id.toString() + "/draft", true, true);
    setMovementList((prev: any) =>
      prev
        .filter((m: any) => m.id !== id)
        .map((m: any) => ({
          ...m,
          additional_movement_ids: m.additional_movement_ids?.filter(
            (additionalMovementId: number) => additionalMovementId !== id,
          ),
        })),
    );
  };

  const handleMovementCancel = async (
    id: number,
    restoreTargetStatus?: OrderRestoreStatus,
  ) => {
    const response = await updateMovement(
      `${id}/cancel`,
      (restoreTargetStatus
        ? { restore_target_status: restoreTargetStatus }
        : {}) as any,
      true,
      false
    );

    if (response?.status === STATUS_CODES.OK) {
      setMovementList((prev) =>
        prev.map((movement) =>
          movement.id === id
            ? { ...movement, deleted_at: new Date().toISOString() }
            : {
                ...movement,
                additional_movement_ids:
                  movement.additional_movement_ids?.filter(
                    (additionalMovementId) => additionalMovementId !== id,
                  ),
              },
        )
      );
      showSnackBar(
        response.data?.message ?? "Movimiento anulado correctamente.",
        "success"
      );
      return true;
    } else {
      showSnackBar(
        response?.data?.message ?? "Error al anular el movimiento.",
        "error"
      );
      return false;
    }
  };

  const handleAdditionalMovementsCreated = (createdMovements: Movement[]) => {
    setMovementList((prev) => {
      const createdIds = new Set(createdMovements.map((m) => m.id));

      return [
        ...createdMovements,
        ...prev.filter((movement) => !createdIds.has(movement.id)),
      ];
    });
  };

  useEffect(() => {
    setMovementList(movements);
  }, [movements]);

  useEffect(() => {
    setMovementList(movements);
  }, [movements]);

  useEffect(() => {
    if (showToast) {
      showSnackBar(toastMessage, toastSeverity);
      setShowToast(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showToast]);

  return (
    <>
      <Grid container gap={2}>
        {movementList.map((movement) => (
          <OperationsLogsCard
            key={movement.id}
            movement={movement}
            client={client}
            stores={stores}
            users={users}
            associatedClients={associatedClients}
            onUpdate={handleMovementUpdate}
            onDelete={handleMovementDelete}
            onCancel={handleMovementCancel}
            onAdditionalMovementsCreated={handleAdditionalMovementsCreated}
          />
        ))}
      </Grid>
    </>
  );
}
